/* The field frame: one recipe for the three text controls (Input,
 * NumberInput, Textarea) and the size of the Select and DatePicker triggers.
 * Engine tier and NOT exported from the barrel. The atoms and those two
 * molecules read it, so a change to a field's frame, size or affix is made
 * here once.
 *
 * `variant` and `size` are the two axes Button uses:
 *  - `variant="default"` is today's framed field. `ghost` has no border and no
 *    fill but keeps its box, paints the hover overlay, and draws the focus
 *    line on the frame while the control is focus-visible. An invalid ghost
 *    field gets a red underline. `readonly` is the framed field on the raised
 *    fill with secondary text. The atom also sets native `readOnly`, so the
 *    look and the behaviour agree. Native `readOnly` alone keeps the default
 *    look.
 *  - `size` is the control ramp: `sm` is the 28px row and `md` today's 36px
 *    row, level with Button of the same size. `inline` has no height and no
 *    padding of its own and takes the surrounding font size, weight and
 *    line-height (Tailwind's preflight sets `font: inherit` on form controls,
 *    so the recipe only drops the text size). Inline is typed to ghost only.
 *
 * The default strings are today's classes VERBATIM and in today's order: the
 * zero-diff VR baselines depend on it. Focus is the text-field look (ADR-0092
 * d8), not the controls' focus-ring: `focus-frame` (`focus-frame-invalid`)
 * turns a framed field's border teal (red) with a faint glow, and
 * `focus-field` draws a 1px line and the glow around the borderless ghost
 * inline field. Input and NumberInput paint it on the wrapper (the utilities'
 * `:has(:focus-visible)` form); Textarea paints it on the element. An open
 * trigger takes it from `aria-expanded="true"`. Literal class strings so
 * Tailwind sees them. */
import { cn } from "./utils.js";

/** `"code"` is Textarea-only (`lib/textarea-autoresize.ts`'s caller types it as
 *  exclusive with `"ghost"`/`"readonly"` at the component boundary); Input and
 *  NumberInput's own prop types never admit it, so widening this shared state
 *  type costs them nothing. */
export type FieldVariant = "default" | "ghost" | "readonly" | "code";
export type FieldSize = "sm" | "md" | "inline";

/** The look axes of a text field. `size="inline"` exists only with
 *  `variant="ghost"`: a framed field cannot sit inside running text. */
export type FieldLookProps =
  | {
      /** Visual style. `default` is the framed field. `readonly` is the muted
       *  read-only field and also sets native `readOnly`. */
      variant?: "default" | "readonly" | undefined;
      /** `sm` is the 28px row, `md` the 36px row. */
      size?: "sm" | "md" | undefined;
    }
  | {
      /** `ghost`: no border or fill, hover paint, focus line on the frame. */
      variant: "ghost";
      /** `sm` / `md` keep the 28px / 36px row; `inline` inherits the
       *  surrounding type and takes no height or padding. */
      size?: FieldSize | undefined;
    };

export interface FieldLookState {
  readonly variant?: FieldVariant | undefined;
  readonly size?: FieldSize | undefined;
  readonly invalid?: boolean | undefined;
  readonly disabled?: boolean | undefined;
}

/** Row box and text size per size: the control ramp (sm 28px, md 36px), the
 *  same steps as Button. `inline` has neither. */
const FIELD_BOX: Record<FieldSize, string> = {
  sm: "px-2 h-7",
  md: "px-3 h-9",
  inline: "",
};
const FIELD_TEXT: Record<FieldSize, string> = {
  sm: "text-xs",
  md: "text-sm",
  inline: "",
};
const TEXTAREA_BOX: Record<FieldSize, string> = {
  sm: "px-2 py-1.5 text-xs",
  md: "px-3 py-2 text-sm",
  inline: "",
};

/** Ghost edge per size. `sm` and `md` keep a transparent 1px border, so the
 *  box is the default field's box. `inline` keeps only the bottom edge, which
 *  is where the invalid underline draws. */
const GHOST_EDGE: Record<FieldSize, string> = {
  sm: "border",
  md: "border",
  inline: "border-b",
};

const GHOST_INVALID = "border-b-pb-red";

/** The `focus-ring` outline (2px ring colour, 1px offset), drawn on a wrapper
 *  while the control inside it is focus-visible. FileUpload's strip and
 *  button use it; a text field takes the field look instead. In high
 *  contrast theme.css's UNLAYERED `*:focus-visible` rule rings the control
 *  too, and a layered `focus:outline-none` loses to it, so the frame drops
 *  the control's ring (`!` beats an unlayered rule) and takes that rule's
 *  3px / 2px weight itself: one ring, the frame's. Dark and light unchanged. */
export const FRAME_FOCUS_RING =
  "has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-1 has-[:focus-visible]:outline-ring hc:has-[:focus-visible]:outline-[3px] hc:has-[:focus-visible]:outline-offset-2 hc:[&_input:focus-visible]:outline-none!";

/** The same one high-contrast ring on a ghost or read-only field frame.
 *  Outside high contrast the field look's outline is transparent, so only
 *  the hc: half is spelled here, ring colour included. A default frame keeps
 *  the control's own ring instead. */
const FIELD_FOCUS_HC =
  "hc:has-[:focus-visible]:outline-[3px] hc:has-[:focus-visible]:outline-offset-2 hc:has-[:focus-visible]:outline-ring hc:[&_input:focus-visible]:outline-none!";

/** The field look of a framed field: its border turns teal, or red. */
const frameFocus = (invalid: boolean): string =>
  invalid ? "focus-frame-invalid" : "focus-frame";

/** Wrapper of Input and NumberInput: holds the control and its affixes. */
export function fieldFrameClass({
  variant = "default",
  size = "md",
  invalid = false,
  disabled = false,
}: FieldLookState): string {
  if (variant !== "ghost") {
    const readonly = variant === "readonly";
    return cn(
      "flex items-center gap-2 rounded-md border",
      readonly ? "bg-pb-card-elevated" : "bg-pb-card",
      "transition-colors",
      FIELD_BOX[size],
      invalid ? "border-pb-red/60" : "border-pb-control-border",
      frameFocus(invalid),
      readonly && FIELD_FOCUS_HC,
      disabled && "opacity-60",
    );
  }
  return cn(
    "flex items-center gap-2 rounded-md border-transparent",
    GHOST_EDGE[size],
    FIELD_BOX[size],
    // Inline keeps only the bottom edge, where the invalid underline draws:
    // the line goes around the frame, so the underline stays red.
    size === "inline" ? "focus-field" : frameFocus(invalid),
    FIELD_FOCUS_HC,
    invalid && GHOST_INVALID,
    disabled ? "opacity-60" : "hover:pb-overlay-paint",
  );
}

/** The `<input>` inside the frame. The frame paints focus, so the control
 *  drops its own outline. */
export function fieldControlClass({
  variant = "default",
  size = "md",
}: FieldLookState): string {
  return cn(
    "flex-1 min-w-0 bg-transparent",
    FIELD_TEXT[size],
    variant === "readonly" ? "text-pb-text-secondary" : "text-pb-text",
    "placeholder:text-pb-text-muted focus:outline-none disabled:cursor-not-allowed",
  );
}

/** A prefix or suffix beside the control (an icon, a unit). */
export const FIELD_AFFIX_CLASS = "text-pb-text-muted shrink-0";

/** Textarea: it is its own frame, so focus paints on the element (the
 *  framed look, or the line on ghost inline).
 *  `autoResize` swaps the manual resize handle and min-height for
 *  `resize-none overflow-hidden` — `lib/textarea-autoresize.ts` then drives
 *  the element's explicit height and `overflowY` from JS. */
export function textareaFrameClass({
  variant = "default",
  size = "md",
  invalid = false,
  autoResize = false,
}: FieldLookState & { readonly autoResize?: boolean | undefined }): string {
  if (variant !== "ghost") {
    const readonly = variant === "readonly";
    const code = variant === "code";
    return cn(
      "w-full rounded-md border",
      readonly ? "bg-pb-card-elevated" : "bg-pb-card",
      TEXTAREA_BOX[size],
      readonly ? "text-pb-text-secondary" : "text-pb-text",
      code && "font-mono text-xs leading-5",
      "placeholder:text-pb-text-muted",
      autoResize ? "resize-none overflow-hidden" : "resize-y min-h-20",
      "transition-colors",
      invalid ? "border-pb-red/60" : "border-pb-control-border",
      frameFocus(invalid),
    );
  }
  return cn(
    "w-full rounded-md border-transparent bg-transparent text-pb-text placeholder:text-pb-text-muted enabled:hover:pb-overlay-paint",
    size === "inline" ? "focus-field" : frameFocus(invalid),
    autoResize ? "resize-none overflow-hidden" : "resize-y",
    GHOST_EDGE[size],
    TEXTAREA_BOX[size],
    size !== "inline" && !autoResize && "min-h-20",
    invalid && GHOST_INVALID,
  );
}

/* ---- Select and DatePicker triggers (molecules) ----
   They take `size` only (no read-only mode). Each size is the field's row. */
export type TriggerSize = "sm" | "md";

const SELECT_BOX = {
  md: "h-9 bg-pb-card px-3 pe-9 text-sm",
  sm: "h-7 bg-pb-card px-2 pe-7 text-xs",
  inline: "h-7 bg-pb-card-elevated px-2 pe-7 text-body-small font-medium",
} as const;
const SELECT_CHEVRON =
  "pointer-events-none absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-pb-text-muted";

/** Select's trigger box and chevron. `variant="inline"` is always the 28px
 *  row on the raised fill, so it ignores `size`. */
export function selectTriggerLook(
  variant: "default" | "inline",
  size: TriggerSize,
): { readonly box: string; readonly chevron: string } {
  const key = variant === "inline" ? "inline" : size;
  return {
    box: SELECT_BOX[key],
    chevron: cn(SELECT_CHEVRON, key === "md" ? "end-3" : "end-2"),
  };
}

/** DatePicker's trigger. Its `aria-expanded` holds the field look while the
 *  calendar is up; the in-flow calendar icon needs no end padding. */
export function dateTriggerClass({
  size = "md",
  invalid = false,
  disabled,
}: Omit<FieldLookState, "variant" | "size"> & {
  readonly size?: TriggerSize | undefined;
}): string {
  return cn(
    "w-full flex items-center justify-between gap-2 rounded-md border bg-pb-card",
    FIELD_TEXT[size],
    "text-start",
    FIELD_BOX[size],
    "transition-colors active:bg-pb-card-elevated",
    invalid ? "border-pb-red/60" : "border-pb-control-border",
    frameFocus(invalid),
    disabled && "opacity-60 cursor-not-allowed",
  );
}
